"use client";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { KeyRound, Pencil, Plus, RefreshCw, Trash2, FlaskConical } from "lucide-react";
import { useAuthedApi } from "../../api/useAuthedApi";
import { EmptyState } from "../Common/EmptyState";
import { PageHeader } from "../Common/PageHeader";
import { CardSkeletonList } from "../Common/CardSkeleton";
import type { ApiKeyListing, CreatedApiKey } from "../../api/apiKeysApi";
import { CreateEmbedKeyDialog, RevealKeyDialog } from "./CreateEmbedKeyDialog";
import { EditEmbedKeyDialog } from "./EditEmbedKeyDialog";

/** Admin management for embed / API keys (apikeys:manage). List, create
 *  (reveal-once), revoke. The plaintext key is only ever shown by the reveal
 *  modal right after create — the API never returns it again. */
export function EmbedKeyList({ embedded = false }: { embedded?: boolean }) {
  const { apiKeys } = useAuthedApi();
  const apiBase = import.meta.env.VITE_API_URL ?? "";
  const [rows, setRows] = useState<ApiKeyListing[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ApiKeyListing | null>(null);
  const [revealed, setRevealed] = useState<CreatedApiKey | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await apiKeys.list());
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load keys");
      setRows([]);
    }
  }, [apiKeys]);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (id: string) => {
    if (!window.confirm("Revoke this key? Embeds using it stop working immediately."))
      return;
    setBusy(id);
    try {
      await apiKeys.revoke(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to revoke");
    } finally {
      setBusy(null);
    }
  };

  // Rotate = new plaintext secret, SAME row id. End-user identities (keyed
  // by api_key_id) survive. Confirmation is stern because the OLD key stops
  // working the moment rotate returns.
  const rotate = async (row: ApiKeyListing) => {
    if (
      !window.confirm(
        `Rotate "${row.name}"? The current key stops working immediately and the new secret is shown ONCE.`,
      )
    )
      return;
    setBusy(row.id);
    try {
      const out = await apiKeys.rotate(row.id);
      // Reuse the reveal-once dialog. Merge the rotate response (id/prefix/key)
      // with the metadata already on the row so the snippets pick the right
      // auth_mode variant. The reveal dialog reads name + auth_mode from here.
      setRevealed({
        id: out.id,
        prefix: out.prefix,
        key: out.key,
        name: row.name,
        kind: row.kind,
        auth_mode: row.auth_mode,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to rotate");
    } finally {
      setBusy(null);
    }
  };

  const active = (rows ?? []).filter((r) => !r.revoked_at);

  // Dev playground: mint a token from a key + email and live-embed the chat
  // iframe. Static HTML served at the SPA origin; open in a new tab so the
  // workspace state is untouched.
  const openPlayground = () =>
    window.open("/embed-playground.html", "_blank", "noopener");

  return (
    <Box>
      {embedded ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FlaskConical size={16} />}
            onClick={openPlayground}
          >
            Playground
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => setCreateOpen(true)}
          >
            Create key
          </Button>
        </Box>
      ) : (
        <PageHeader
          title="Embed keys"
          subtitle="Keys that let external sites embed this chat, each scoped to a capability profile."
          actions={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<FlaskConical size={16} />}
                onClick={openPlayground}
              >
                Playground
              </Button>
              <Button
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setCreateOpen(true)}
              >
                Create key
              </Button>
            </Stack>
          }
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {rows === null ? (
        <CardSkeletonList />
      ) : active.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No embed keys yet"
          description="Create a key to embed this chat on another site. You choose which skills and MCPs it can use, which origins may load it, and how end-users authenticate."
          action={
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => setCreateOpen(true)}
            >
              Create key
            </Button>
          }
        />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Prefix</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Auth</TableCell>
              <TableCell>Origins</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {active.map((k) => (
              <TableRow key={k.id}>
                <TableCell>{k.name}</TableCell>
                <TableCell>
                  <code>{k.key_prefix}…</code>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={k.kind} />
                </TableCell>
                <TableCell>{k.auth_mode}</TableCell>
                <TableCell>
                  {k.allowed_origins.length ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {k.allowed_origins.slice(0, 2).map((o) => (
                        <Chip key={o} size="small" variant="outlined" label={o} />
                      ))}
                      {k.allowed_origins.length > 2 && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`+${k.allowed_origins.length - 2}`}
                        />
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      none
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{k.model || "Auto"}</TableCell>
                <TableCell>
                  {k.last_used_at
                    ? new Date(k.last_used_at).toLocaleDateString()
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy === k.id}
                        onClick={() => setEditTarget(k)}
                      >
                        <Pencil size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Rotate secret">
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy === k.id}
                        onClick={() => rotate(k)}
                      >
                        <RefreshCw size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Revoke">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={busy === k.id}
                        onClick={() => revoke(k.id)}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CreateEmbedKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          setCreateOpen(false);
          setRevealed(created);
          void load();
        }}
      />
      <EditEmbedKeyDialog
        open={editTarget !== null}
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => {
          setEditTarget(null);
          void load();
        }}
      />
      <RevealKeyDialog
        created={revealed}
        apiBase={apiBase}
        onClose={() => setRevealed(null)}
      />
    </Box>
  );
}
